import type { FC } from "react";
import { createElement } from "react";

import { isClient } from "@/lib/common";
import { useViewportInfoContext } from "@/src/contexts";
import type { IImageLoader } from "@Components/ImageLoader";
import ImageLoader from "@Components/ImageLoader";
import { Link } from "@Components/Link";
import { isNullOrEmpty } from "@Components/Modules/Validations";
import { Device } from "@Components/Modules/ViewportResize";

import * as css from "./BrandImageBanner.module.css";

export interface IBrandImageBanner {
    image: IImageLoader;
    imageUrlTablet: string;
    imageUrlSmallMobile: string;
    imageUrlLargeMobile: string;
    backgroundColor: string;
    imageHeight: number;
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => boolean | void;
    mobileCaption: string;
    onLoaded?: () => void;
    caption: string;
    title: string;
    titleHtmlTag: string;
    description: string;
    additionalDescription: string;
    appearance: BrandImageBannerAppearance;
}

export enum BrandImageBannerAppearance {
    Standard,
    ImageDescription,
}

const brandImageBannerAppearanceToClassname = (appearance: BrandImageBannerAppearance): string =>
    appearance === BrandImageBannerAppearance.ImageDescription ? css.imageDescription : "";

export const BrandImageBanner: FC<IBrandImageBanner> = (props: IBrandImageBanner) => {
    const { device } = useViewportInfoContext();

    let imageUrl: string;
    switch (device) {
        case Device.Mobile: {
            imageUrl =
                !isClient || window.innerWidth < 480 ? props.imageUrlSmallMobile : props.imageUrlLargeMobile;
            break;
        }
        case Device.Tablet:
            imageUrl = props.imageUrlLargeMobile;
            break;
        default:
            imageUrl = props.image.src;
    }

    if (isNullOrEmpty(imageUrl) && props.image) {
        imageUrl = props.image.src;
    }

    if (!props.image || !props.image.src) {
        return null;
    }

    const content = (
        <div
            className={css.banner}
            style={props.backgroundColor ? { backgroundColor: props.backgroundColor } : undefined}
        >
            <div className={css.standardContainer}>
                <div className={css.imageContainer}>
                    <ImageLoader {...props.image} src={imageUrl} onLoaded={props.onLoaded} />
                </div>
                <div className={css.mainContainer}>
                    {props.mobileCaption && device === Device.Mobile ? (
                        <h1 className={css.mobileCaption}>{props.mobileCaption}</h1>
                    ) : null}
                    {props.caption && device !== Device.Mobile ? (
                        <h1 className={css.caption}>{props.caption}</h1>
                    ) : null}
                    {props.title && device !== Device.Mobile
                        ? createElement(props.titleHtmlTag || "div", { className: css.title }, props.title)
                        : null}
                    {props.description && device !== Device.Mobile ? (
                        <div className={css.description}>{props.description}</div>
                    ) : null}
                    {props.additionalDescription ? (
                        <div className={css.additionalDescriptionContainer}>
                            <div className={css.additionalDescription}>{props.additionalDescription}</div>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );

    return (
        <div
            className={`${css.brandImageBannerComponent} ${brandImageBannerAppearanceToClassname(props.appearance)}`}
        >
            {props.onClick ? <Link onClick={props.onClick}>{content}</Link> : content}
        </div>
    );
};
